import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import HomeRecentWorkQuery from './__generated__/HomeRecentWorkQuery.graphql.ts';
import type {
	HomeRecentAgentSessionsQuery$data,
	HomeRecentAgentSessionsQuery$variables,
} from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import HomeRecentAgentSessionsQuery from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import type {
	HomeRecentRovoActivationQuery$data,
	HomeRecentRovoActivationQuery$variables,
} from './__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import HomeRecentRovoActivationQuery from './__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import type {
	RecentListHomeRecentWorkV3Query as RecentListHomeRecentWorkV3Query$data,
	RecentListHomeRecentWorkV3QueryVariables as RecentListHomeRecentWorkV3Query$variables,
	RecentListHomeRecentWorkV3Query_Query,
} from './__types__/RecentListHomeRecentWorkV3Query.ts';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers.ts';

export const useHomeRecentWorkData = ({
	isWhiteboardViewedEnabled,
	isDatabaseEnabled,
	isSlidesEnabled,
	first,
}: {
	isWhiteboardViewedEnabled: boolean;
	isDatabaseEnabled: boolean;
	isSlidesEnabled?: boolean;
	first?: number;
}): {
	data: RecentListHomeRecentWorkV3Query_Query | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { cloudId, isLoggedIn } = useSessionData();
	const { data, loading, error } = useQueryInterop<
		RecentListHomeRecentWorkV3Query$data,
		RecentListHomeRecentWorkV3Query$variables
	>(HomeRecentWorkQuery, {
		...getRecentWorkQueryOptions({
			cloudId,
			isLoggedIn,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			isSlidesEnabled,
			first,
		}),
		interopApolloAggClient: true,
	});

	return { data, loading, error };
};

export const useHomeRecentAgentSessionsData = ({ first = 2 }: { first?: number } = {}): {
	data: HomeRecentAgentSessionsQuery$data['agent_sessionQuery'] | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const { cloudId, isLoggedIn, userId } = useSessionData();
	const {
		data: rovoActivationData,
		loading: rovoActivationLoading,
		error: rovoActivationError,
	} = useQueryInterop<HomeRecentRovoActivationQuery$data, HomeRecentRovoActivationQuery$variables>(
		HomeRecentRovoActivationQuery,
		{
			variables: { cloudId },
			fetchPolicy: 'cache-first',
			interopApolloAggClient: true,
			skip: !isLoggedIn || !userId,
		},
	);

	const rovoActivationId = rovoActivationData?.tenantContexts?.[0]?.activationIdByProduct?.active;
	const escapedUserId = userId?.replace(/\\/g, '\\\\').replace(/'/g, "\\'") || '';
	const invocationTypeFilter =
		"invocationType = 'CONFLUENCE_COMMENT_MENTION'" +
		" OR invocationType = 'CONFLUENCE_CHAT_EDIT'" +
		" OR invocationType = 'CONFLUENCE_INLINE_EDIT'" +
		" OR invocationType = 'CONFLUENCE_CWR_EDIT'" +
		" OR invocationType = 'CONFLUENCE_CWR_CREATE'" +
		" OR invocationType = 'CONFLUENCE_CWR_EXISTING'" +
		" OR invocationType = 'CONFLUENCE_REMIX'" +
		" OR invocationType = 'CONFLUENCE_REMIX_EDIT'" +
		" OR invocationType = 'CONFLUENCE_CHAT'";
	const aqlQuery =
		`authorAccountId = '${escapedUserId}'` +
		" AND (state = 'INPUT_REQUIRED' OR state = 'AUTH_REQUIRED')" +
		` AND (${invocationTypeFilter})`;

	const {
		data: agentSessionsData,
		loading: agentSessionsLoading,
		error: agentSessionsError,
	} = useQueryInterop<HomeRecentAgentSessionsQuery$data, HomeRecentAgentSessionsQuery$variables>(
		HomeRecentAgentSessionsQuery,
		{
			variables: {
				aqlQuery,
				shardingContext: `ari:cloud:rovo::workspace/${rovoActivationId || ''}`,
				first,
			},
			interopApolloAggClient: true,
			skip: !isLoggedIn || !userId || !rovoActivationId,
		},
	);

	return {
		data: agentSessionsData?.agent_sessionQuery ?? undefined,
		loading: rovoActivationLoading || agentSessionsLoading,
		error: rovoActivationError || agentSessionsError,
	};
};
